import Service, { service } from '@ember/service'
import { ANZ, APAC, EMEA, AMER } from 'teacher-ui-client/feature-control/marketing-regions'
import type SessionService from 'teacher-ui-client/services/session'
import type PartnerService from 'teacher-ui-client/services/partner'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type FeaturesService from 'teacher-ui-client/services/features'
import { PartnerCode } from 'teacher-ui-client/partner/partners'

/**
 * A directory of feature access names / ids.
 *
 * Intentionally separate to products, as a feature access may be restricted to part of a product, or extend across
 * multiple.
 * Intentionally separate to feature flag names as we use them in conjunction with other logic to determine access. Also
 * feature access may be solely determined by logic other than flags.
 *
 * They're named to describe the feature being restricted. These are intended to be longer-lived than feature flags.
 */
export const FeatureAccess = {
  MathseedsPrimeProduct: 'msp_product',
  WritingArea: 'writing_area',
  CreateTrialSubscriptions: 'create_trial_subscriptions',
  OneRoster: 'one_roster',
  QuickStart: 'quick_start',
} as const

export type FeatureAccessId = (typeof FeatureAccess)[keyof typeof FeatureAccess]

/**
 * A service to help handle feature access. Intended to handle feature access logic regarding;
 * - countries/regions
 * - partners
 * - feature flags (runtime toggling of behaviour)
 *
 * Works similarly to feature flags service but takes into account other aspects that might impact whether a feature is
 * accessible to a user, allowing us to;
 * - maintain access logic on partially released features (eg a region-based rollout)
 * - expand to more regions by toggling a feature flag (eg released to AU, but FF toggle gives worldwide access)
 * - use a single feature access lock across multiple feature flags (where it makes sense to do so)
 * - have clearly defined & tested rules for access
 *
 * With this service, we keep feature flags as a simple, short-lived, runtime behaviour toggle.
 *
 * There is also a companion helper available {{#if (can-access "writing_area")}} ....
 *
 * @example <caption>partially released feature to single region</caption>
 * ```
 * // canAccess logic
 * case 'writing_area':
 *   return ANZ.includes(countryCode)
 *
 * // usage
 * get showWritingTab() {
 *   return this.featureControl.canAccess('writing_area')
 * }
 * ```
 *
 * @example <caption>expand access to more regions with a feature flag</caption>
 * // canAccess logic
 * case 'writing_area':
 *   if (this.features.isEnabled('writingEmeaFF')) {
 *     return ANZ.includes(countryCode) || EMEA.includes(countryCode)
 *   }
 *   return ANZ.includes(countryCode)
 *
 * // usage
 * get showWritingTab() {
 *   return this.featureControl.canAccess('writing_area')
 * }
 *
 * @example <caption>use a feature access lock across multiple feature flags</caption>
 * // canAccess logic
 * case 'msp_product':
 *   return APAC.includes(countryCode)
 *
 * // usage
 * get showMspMeasurement() {
 *   return this.featureControl.canAccess('msp_product') && this.features.isEnabled('mspMeasurementFF')
 * }
 * get showMspGrade3() {
 *   return this.featureControl.canAccess('msp_product') && this.features.isEnabled('mspGrade3FF')
 * }
 */
export default class FeatureControlService extends Service {
  @service session!: SessionService

  @service partner!: PartnerService

  @service subscriptionType!: SubscriptionTypeService

  @service features!: FeaturesService

  canAccess(featureId: FeatureAccessId): boolean {
    const { countryCode } = this.session.currentSchool ?? {}
    switch (featureId) {
      case FeatureAccess.QuickStart: {
        if (ANZ.includes(countryCode)) {
          return true
        }
        if (APAC.includes(countryCode)) {
          return this.features.isEnabled('quick_start_apac_access')
        }
        if (EMEA.includes(countryCode)) {
          return this.features.isEnabled('quick_start_emea_access')
        }
        if (AMER.includes(countryCode)) {
          return this.features.isEnabled('quick_start_amer_access')
        }
        return false
      }
      case FeatureAccess.WritingArea:
        return (
          !this.session.currentSchool.isHomeschool &&
          (ANZ.includes(countryCode) || EMEA.includes(countryCode) || APAC.includes(countryCode))
        )
      case FeatureAccess.MathseedsPrimeProduct:
        return APAC.includes(countryCode) || ['au', 'uk', 'nz'].includes(this.session.currentSchool.accent.extension)
      case FeatureAccess.CreateTrialSubscriptions:
        return Boolean(
          this.subscriptionType.current &&
          !this.partner.hasPartner(PartnerCode.RatnaSagar, this.subscriptionType.current),
        )
      case FeatureAccess.OneRoster:
        return Boolean(this.session.currentSchool.oneRosterEnabledAt)
      default:
        return false
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'feature-control': FeatureControlService
  }
}
